/**
 * PreviewIndex — a simple hub linking every /preview/* design-review surface.
 * Themed via the shared previewTheme (light/dark toggle, 1.25 zoom). Not a
 * shipped tool — design review only.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIF, BODY, MONO, PREVIEW_ZOOM, useTheme, ThemeToggle } from './preview/previewTheme';

const SCREENS: { label: string; sub: string; to: string; themes: string }[] = [
  { label: 'Work Desk', sub: 'Internal accountant landing — metrics, active claims, queue', to: '/preview/workdesk', themes: 'light + dark' },
  { label: 'Client Portal', sub: 'Insured-facing status, documents, clarifications', to: '/preview/client-portal', themes: 'light + dark' },
  { label: 'Attorney Dashboard', sub: 'Matters portfolio, demands, deadlines, generators', to: '/preview/attorney-dashboard', themes: 'light + dark' },
  { label: 'Data Specialist', sub: 'Transaction review queue, wizards, ingestion', to: '/preview/data-specialist', themes: 'light + dark' },
  { label: 'War Room', sub: 'Multi-agent deliberation, consensus, run log', to: '/preview/war-room', themes: 'light + dark' },
  { label: 'Document Hub', sub: 'Library, document detail, RAG provenance', to: '/preview/document-hub', themes: 'light + dark' },
  { label: 'Admin Orchestrator', sub: 'Pipeline stages, runs, approvals, health', to: '/preview/admin-orchestrator', themes: 'light + dark' },
  { label: 'External Accountant', sub: 'Outside attester — indemnity, engines, sign-off', to: '/preview/external-accountant', themes: 'light + dark' },
];

export default function PreviewIndex() {
  const navigate = useNavigate();
  const { theme, P, toggle } = useTheme();

  return (
    <div style={{ zoom: PREVIEW_ZOOM, minHeight: '100vh', width: '100%', background: P.pageBg, fontFamily: BODY, color: P.textBody }}>
      <header style={{ height: 62, background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 28px', gap: 14, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20 }}>The Figure Co.</div>
          <span style={{ marginLeft: 6, fontSize: 11, letterSpacing: '1.2px', textTransform: 'uppercase', color: P.textMuted, fontFamily: MONO }}>Design Preview</span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
        </div>
      </header>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 28px 60px' }}>
        <h1 style={{ fontFamily: SERIF, fontSize: 32, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Redesign preview</h1>
        <p style={{ margin: '8px 0 28px', color: P.textLabel, fontSize: 15 }}>
          Eight screens rendered to the supplied templates, in both Daylight and Dark. Click any card; use the ☀/☾ toggle to flip themes. These are design-review surfaces — the look is the deliverable, not the wiring.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {SCREENS.map((s) => (
            <div
              key={s.to}
              onClick={() => navigate(s.to)}
              style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, padding: '20px 22px', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = P.orangeText; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = P.cardBorder; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400 }}>{s.label}</h2>
                <span style={{ fontSize: 11, fontWeight: 600, color: P.textMuted, fontFamily: MONO }}>{s.themes}</span>
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 13, color: P.textLabel, lineHeight: 1.4 }}>{s.sub}</p>
              <div style={{ marginTop: 14, fontSize: 12.5, color: P.orangeText, fontWeight: 600 }}>Open {s.to} ›</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
